import { SubscriptionPlan } from '@repo/domain/user';
import { FreeTrialData } from '@repo/editor/src/hooks/useFreeTrial';
import { format } from 'date-fns';
import capitalize from 'lodash/capitalize';

/**
 * It's the status of some king of banner that has been displayed.
 * The logic behind it is the following: if the user has closed the banner - it should not be displayed again.
 * However, if the status (thus, banner text) has been changed - it should be shown to notify about other status peculiarities.
 */
export enum FreeTrialSeenStatus {
  CancelledOngoing = 'cancelled-ongoing',
  CancelledEnded = 'cancelled-ended',
  Active = 'active',
}

const expiresSoon = (date: Date): boolean => {
  const hoursLeft = (date.getTime() - Date.now()) / 1000 / 60 / 60;
  return hoursLeft <= 48;
};

export const getInfoboxData = (
  freeTrial: FreeTrialData,
  plan?: SubscriptionPlan
): [string, string, FreeTrialSeenStatus | null] => {
  const planName = plan ? capitalize(plan) : '';
  let title = '';
  let text = '';
  let status: FreeTrialSeenStatus | null = null;

  if (freeTrial.isCancelled && freeTrial.isRunning) {
    title = `You can continue using your ${planName} plan until ${format(
      freeTrial.trialEndDate,
      'MMM d, yyyy'
    )}.`;
    text =
      'Once your trial period ends, you will automatically be enrolled into the Free plan.';
    status = FreeTrialSeenStatus.CancelledEnded;
  } else if (freeTrial.isCancelled) {
    title = 'Your free trial has ended.';
    text =
      'You are now on the Free plan. Resubscribe anytime to the plan that works best for you.';
    status = FreeTrialSeenStatus.CancelledEnded;
  } else if (freeTrial.isRunning && expiresSoon(freeTrial.trialEndDate)) {
    title = 'You trial ends soon.';
    text = `Your ${planName} plan subscription will automatically begin on ${format(
      freeTrial.trialEndDate,
      'MMM d, yyyy HH:mm'
    )} unless you cancel.`;
    status = FreeTrialSeenStatus.Active;
  }

  return [title, text, status];
};
